"use client";

import { usePathname, useSearchParams, useRouter } from "next/navigation";
import { I18nextProvider } from "react-i18next";
import i18n from "@/i18n";

import LoginHeader from "@/app/components/LoginHeader";
import AppHeader from "@/app/components/AppHeader";
import AppLayout from "@/app/components/AppLayout";
import { useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { usePagination } from "@/context/PaginationContext";
import Spinner from "./components/Spinner";
import Toast from "./components/ui/Toast";
import { useInformation } from "@/context/InformationContext";
import Modal from "./components/Modal";
import NewsGates from "./components/news/NewsGates";
import { Suspense } from "react";
import { conversationsApi } from "@/api/conversations";
import { useActivityTracker } from "@/customHooks/useActivityTracker";
import { useAuthRedirect } from "@/customHooks/useAuthRedirect";
import EnvironmentBanner from "./components/EnvironmentBanner";
import RegeneratePollingRunner from "./components/RegeneratePollingRunner";
import { useConfigStore } from "@/store/configStore";
import { isPublicRoute } from "@/constants/routeGuards";

export default function ClientLayoutWrapper({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <Suspense>
      <ClientLayout>{children}</ClientLayout>
    </Suspense>
  );
}

function ClientLayout({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isLogin = isPublicRoute(pathname);
  const isTraining = pathname.startsWith("/training");
  const { isAppLoading, loginWithLTIToken } = useAuth();
  const { notification, modal, setModal, setHasConversations } =
    useInformation();
  const { user } = useAuth();
  const { isHydrated } = usePagination();
  const { fetchConfig, isLoaded: isConfigLoaded } = useConfigStore();
  const shouldBlockMainContent =
    !isLogin && (isAppLoading || !isHydrated || !isConfigLoaded);

  // Fetch config variables on app load
  useEffect(() => {
    if (!isConfigLoaded) {
      fetchConfig();
    }
  }, [fetchConfig, isConfigLoaded]);

  // Initialize activity tracker for authenticated users
  useActivityTracker({
    inactivityThreshold: 5 * 60 * 1000, // 5 minutes of inactivity
    checkInterval: 60 * 1000, // Check every minute
    refreshThreshold: 10 * 60 * 1000, // Refresh if token expires in 10 minutes
  });

  // Global guard: keep authenticated users out of unauthenticated-only
  // pages (login / register / forgot-password). See `routeGuards.ts`.
  useAuthRedirect();

  useEffect(() => {
    if (!user) return;

    // determine hasConversations flag if user
    const fetchConversations = async () => {
      const response = await conversationsApi.getMyConversations(1, 10);
      if (response.data.length > 0) {
        setHasConversations(true);
      } else {
        setHasConversations(false);
      }
    };

    fetchConversations();
  }, [user, setHasConversations]);

  useEffect(() => {
    // if the route is /, and no token is in the search params, redirect to /login
    const hasTokenInSearchParams = searchParams.get("token");
    if (pathname === "/" && !hasTokenInSearchParams) {
      router.replace("/login");
    }
    // token in search params means possible LTI connection request
    if (hasTokenInSearchParams) {
      const token = searchParams.get("token");
      if (token) {
        loginWithLTIToken(token)
          .then(() => {
            router.replace("/dashboard");
          })
          .catch(() => {
            router.replace("/login");
          });
      }
    }
  }, [router, pathname, searchParams, loginWithLTIToken]);

  return (
    <I18nextProvider i18n={i18n}>
      <RegeneratePollingRunner />
      <EnvironmentBanner />
      {isLogin || (!isAppLoading && !user) ? <LoginHeader /> : <AppHeader />}
      <main
        className={`${isTraining ? "h-screen overflow-hidden" : "h-screen"}`}
      >
        {shouldBlockMainContent ? (
          <div className="flex items-center justify-center h-[90dvh]">
            <Spinner />
          </div>
        ) : (
          <AppLayout>{children}</AppLayout>
        )}
      </main>

      {notification && (
        <Toast
          type={notification.type}
          message={notification.message}
          isVisible={notification.isVisible}
          onClose={notification.onClose}
          position={notification.position}
          duration={notification.duration}
          className={notification.className}
        />
      )}

      <NewsGates enabled={!isLogin && !!user && !shouldBlockMainContent} />

      {modal && typeof modal === "object" && (
        <Modal
          type={modal.type}
          title={modal.title}
          body={modal.body}
          acceptButtonText={modal.acceptButtonText}
          declineButtonText={modal.declineButtonText}
          onAccept={modal.onAccept}
          onDecline={modal.onDecline}
          setIsOpen={setModal}
          hideCrossIcon={modal.hideCrossIcon}
          onClose={modal.onClose}
        />
      )}
    </I18nextProvider>
  );
}
